import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const DISMISS_KEY = "vault.signin.reminder.dismissed";

/**
 * Slim, single-line reminder shown to anonymous visitors on every page EXCEPT
 * the homepage. Dismissible per session.
 *
 * Rewritten 2026-02 to fit on ONE LINE on desktop (was a 4-line monologue
 * eating ~15% of viewport height).
 */
export default function SignInReminder() {
  const { user, ready } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  // Hide on homepage (it has its own signup panel), while auth is loading,
  // when the user is already signed in, or when the user dismissed it.
  if (!ready) return null;
  if (user) return null;
  if (pathname === "/" || pathname === "") return null;
  if (dismissed) return null;

  const openSignup = () =>
    window.dispatchEvent(
      new CustomEvent("open-auth", { detail: { mode: "signup" } })
    );

  return (
    <div
      data-testid="signin-reminder"
      className="bg-purple-500/[0.06] border-b border-purple-400/20 text-purple-200 font-mono no-print"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-1.5 flex items-center gap-3">
        <Lock size={11} className="shrink-0 text-purple-400" />
        <p className="flex-1 truncate text-[10px] sm:text-[11px] tracking-[0.18em] uppercase">
          <span className="text-purple-400">ANON MODE</span>
          <span className="hidden sm:inline text-purple-300/60"> · saves live in this browser only · no DMs · no profile</span>
          <span className="sm:hidden text-purple-300/60"> · local-only</span>
          <button
            type="button"
            data-testid="reminder-signup-btn"
            onClick={openSignup}
            className="ml-2 sm:ml-3 underline underline-offset-4 text-white hover:text-purple-300 transition-colors"
          >
            Make a free account
          </button>
        </p>
        <button
          type="button"
          data-testid="reminder-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss reminder"
          className="shrink-0 text-purple-400/50 hover:text-purple-200 transition-colors"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
